import * as yup from "yup";
import {
    AncillaryExceptionBaseDefinitionFormValues,
    AncillaryExceptionEntityRequest,
    AncillaryExceptionEntityResponse
} from "../../../api/types/ancillaryException";
import dayjs from "dayjs";
import {SelectElementModelBE, SelectElementModelFE} from "../../../api/types/generic";
import {CommercialCategorySelectFE} from "../../../api/types/commercialCategory";
import {
    fromGenericBeToGenericFe,
    fromGenericFeToGenericBe,
    fromSingleGenericFeToSingleGenericBe,
    i18nConvertNumberWithDecimals
} from "../../../utils";
import {CommercialSegmentEntityResponse, CommercialSegmentSelectFE} from "../../../api/types/commercialSegment";
import {yupPickupFromFunc, yupPickupToFunc, yupReservationFromFunc, yupReservationToFunc} from "../../../yupUtils";
import {maxNumberYupLimit, minNumberYupLimit} from "../../../constants";
import {ExceptionListEntityResponse} from "../../../api/types/exceptionList";
import {convertToCents} from "../../../../../util/state/commonFunctions";

export const initialAncillaryExceptionBaseDefinitionValues: AncillaryExceptionBaseDefinitionFormValues = {
    prcAncillary: null,

    discountIncreaseSelected: "discountIncreasePercentage",
    discountIncreasePercentage: null,
    discountIncreaseFixed: null,
    name: "",

// FIELDS FROM ANCILLARY
    commercialCategory: null,
    note: null,
    reservationFrom: "",
    reservationTo: "",
    pickupFrom: "",
    pickupTo: "",
    tpUnit: "",
    currency: "EUR",
    amountMin: null,
    amountMax: null,
    chargingWay: "",
    chargeType: "",
    maxUnits: null,
    commercialSegment: null,
    code: "",
    nameAncillary: "",
    description: "",
    type: "",
    exceptionListPack: null,
    subtype: "",
    cardinality: null,
    sellDesk: false,
    passageToCounterNeeded: true,
    applicationTiming: null,
    toleranceTime: null,
    flagResellableAtDeskPostRa: false,
    flagResellableInSubstitution: false,
    flagDiscountApplicable: false,
    timeToChange: null,
    timeToRemove: null,
    additionalField: null,
    tax: false,
    taxCode: "",
    dealer: [],
    legalEntity: []
};

export const getAncillaryExceptionBaseDefinitionValidationSchema = (t: Function, ancillaryException: AncillaryExceptionEntityResponse | null) => {
    const yupReservationFrom = yupReservationFromFunc({
        t,
        id: ancillaryException?.id || null,
        reservationFrom: ancillaryException?.reservationFrom as string
    });
    const yupReservationTo = yupReservationToFunc({
        t,
        id: ancillaryException?.id || null,
        reservationTo: ancillaryException?.reservationTo as string,
        reservationFromFieldName: "reservationFrom"
    });
    const yupPickupFrom = yupPickupFromFunc({
        t,
        id: ancillaryException?.id || null,
        pickupFrom: ancillaryException?.pickupFrom as string
    });
    const yupPickupTo = yupPickupToFunc({
        t,
        id: ancillaryException?.id || null,
        reservationTo: ancillaryException?.reservationTo as string,
        pickupFrom: ancillaryException?.pickupFrom as string,
        pickupTo: ancillaryException?.pickupTo as string,
        pickupFromFieldName: "pickupFrom"
    });

    return yup.object().shape({
        reservationFrom: yupReservationFrom,
        reservationTo: yupReservationTo,
        pickupFrom: yupPickupFrom,
        pickupTo: yupPickupTo,
        unit: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string()
            })
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        dealer: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string()
            })
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        legalEntity: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string()
            })
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        commercialCategory: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string()
            })
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        commercialSegment: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string()
            })
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        currency: yup
            .string()
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        amountMin: yup.number()
            .nullable()
            .min(0, t("pricing:validation.positiveNumber"))
            .transform((o, v) => v ? convertToCents(v) : v)
            .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit),
        amountMax: yup.number()
            .nullable()
            .transform((o, v) => v ? convertToCents(v) : v)
            .min(yup.ref("amountMin"), ({min}) => t("pricing:validation.numberGreaterThan") + min)
            .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit),
        maxUnits: yup.number()
            .nullable()
            .min(0, t("pricing:validation.positiveNumber"))
            .integer()
            .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit),
        timeToChange: yup
            .number()
            .nullable()
            .integer()
            .min(0, t("pricing:validation.positiveNumber"))
            .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit),
        timeToRemove: yup
            .number()
            .nullable()
            .integer()
            .min(0, t("pricing:validation.positiveNumber"))
            .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit),
        name: yup.string().nullable().required(t("translation:validation.fieldMandatory")),
        prcAncillary: yup.object()
            .shape({
                code: yup.string(),
                label: yup.string()
            }).nullable().required(t("translation:validation.fieldMandatory")),
        chargeType: yup.string().nullable().required(t("translation:validation.fieldMandatory")),
        chargingWay: yup.string().nullable().required(t("translation:validation.fieldMandatory")),
        discountIncreasePercentage: yup
            .number()
            .integer()
            .nullable()
            .typeError(t("pricing:alert.errors.integer"))
            .min(-99, t("pricing:validation.numberGreaterThan") + -99)
            .max(100, t("pricing:validation.numberLowerThan") + 100),
        discountIncreaseFixed: yup
            .number()
            .transform((o, v) => v ? parseFloat(v.replace(/,/g, ".")) : v)
            .nullable()
            .typeError(t("pricing:alert.errors.integer"))
            .min(minNumberYupLimit, t("pricing:validation.numberGreaterThan") + minNumberYupLimit)
            .max(maxNumberYupLimit, t("pricing:validation.numberLowerThan") + maxNumberYupLimit),
        exceptionListPack: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string()
            })
            .nullable()
            .when('type', {
                is: (type: string) => type === 'Pack',
                then: yup
                    .object()
                    .shape({
                        code: yup.string(),
                        label: yup.string()
                    })
                    .nullable()
                    .required(t("translation:validation.fieldMandatory")),
                otherwise: yup
                    .object()
                    .shape({
                        code: yup.string(),
                        label: yup.string()
                    })
                    .nullable()
            }),
    });
};

export const buildEntityRequestFromFormValues = (watchAllFields: AncillaryExceptionBaseDefinitionFormValues, exceptionPack: ExceptionListEntityResponse | null): AncillaryExceptionEntityRequest => {
    return {
        amountMin: watchAllFields?.amountMin ? watchAllFields.amountMin  : null,
        amountMax: watchAllFields?.amountMax ? watchAllFields.amountMax  : null,
        chargeType: watchAllFields.chargeType,
        chargingWay: watchAllFields.chargingWay || "",
        code: String(watchAllFields.prcAncillary?.ancillaryCode),
        currency: watchAllFields.prcAncillary?.currency || "",
        description: watchAllFields.description || "",
        note: !!watchAllFields?.note ? watchAllFields.note as string : null,
        pickupFrom: dayjs(watchAllFields.pickupFrom).toISOString(),
        pickupTo: dayjs(watchAllFields.pickupTo).toISOString(),
        reservationFrom: dayjs(watchAllFields.reservationFrom).toISOString(),
        reservationTo: dayjs(watchAllFields.reservationTo).toISOString(),
        timeUnitsMax: watchAllFields.maxUnits === 0 ? null : watchAllFields.maxUnits as number,
        type: watchAllFields.prcAncillary?.type || "",
        exceptionListPack: exceptionPack ? exceptionPack : null,
        subtype: watchAllFields.prcAncillary?.subtype || "",
        name: watchAllFields.name,
        tpUnit: watchAllFields.prcAncillary?.tpUnit as string,
        dealer: (watchAllFields.dealer as SelectElementModelFE).code as string,
        legalEntity: (watchAllFields.legalEntity as SelectElementModelFE).code as string,
        commercialCategory: {
            id: watchAllFields?.commercialCategory?.code || "",
            name: watchAllFields?.commercialCategory?.label || "",
            commercialSegment: watchAllFields?.commercialCategory?.commercialSegment as CommercialSegmentEntityResponse,
            active: watchAllFields?.commercialCategory?.active as boolean,
        },
        cardinality: watchAllFields?.cardinality as number,
        prcAncillary: {
            ...watchAllFields.prcAncillary,
            id: watchAllFields.prcAncillary?.code as number,
            code: watchAllFields.prcAncillary?.ancillaryCode as string,
            reservationFrom: dayjs(watchAllFields.prcAncillary?.reservationFrom).toISOString() as string,
            reservationTo: dayjs(watchAllFields.prcAncillary?.reservationTo).toISOString() as string,
            pickupFrom: dayjs(watchAllFields.prcAncillary?.pickupFrom).toISOString() as string,
            pickupTo: dayjs(watchAllFields.prcAncillary?.pickupTo).toISOString() as string,
            tpUnit: watchAllFields.prcAncillary?.tpUnit as string,
            currency: watchAllFields.prcAncillary?.currency as string,
            amountMin: watchAllFields.prcAncillary?.amountMin ? watchAllFields.prcAncillary?.amountMin : null,
            amountMax: watchAllFields.prcAncillary?.amountMax ? watchAllFields.prcAncillary?.amountMax : null,
            dealerId: watchAllFields.prcAncillary?.dealerId as number,
            chargingWay: watchAllFields.prcAncillary?.chargingWay as string,
            maxUnits: watchAllFields.prcAncillary?.maxUnits === 0 ? null : watchAllFields.prcAncillary?.maxUnits as number,
            chargeType: watchAllFields.prcAncillary?.chargeType as string,
            nameAncillary: watchAllFields.prcAncillary?.nameAncillary as string,
            description: watchAllFields.prcAncillary?.description as string,
            type: watchAllFields.prcAncillary?.type as string,
            //exceptionListPack: watchAllFields.prcAncillary?.exceptionListPack as ExceptionListEntityResponse,
            subtype: watchAllFields.prcAncillary?.subtype as string,
            segments: fromGenericFeToGenericBe(watchAllFields.prcAncillary?.segments as SelectElementModelFE[]),
            cardinality: watchAllFields.prcAncillary?.cardinality as number,
            sellDesk: watchAllFields.prcAncillary?.sellDesk as boolean,
            passageToCounterNeeded: watchAllFields.prcAncillary?.passageToCounterNeeded as boolean,
            applicationTiming: watchAllFields.prcAncillary?.applicationTiming === null ? null : watchAllFields.prcAncillary?.applicationTiming === "PICK UP" ? "PICK_UP" : "DROP_OFF",
            toleranceTime: watchAllFields.prcAncillary?.toleranceTime as number,
            flagResellableAtDeskPostRa: watchAllFields.prcAncillary?.flagResellableAtDeskPostRa ?? false,
            flagResellableInSubstitution: watchAllFields.prcAncillary?.flagResellableInSubstitution ?? false,
            flagDiscountApplicable: watchAllFields.prcAncillary?.flagDiscountApplicable ?? false,
            timeToChange: watchAllFields.prcAncillary?.timeToChange ?? null,
            timeToRemove: watchAllFields.prcAncillary?.timeToRemove ?? null,
            additionalField: watchAllFields.prcAncillary?.additionalField as string,
            tax: watchAllFields.prcAncillary?.tax as boolean,
            taxCode: watchAllFields.prcAncillary?.taxCode as string,
            isOneWay: watchAllFields.prcAncillary?.isOneWay as boolean,
            dealer: (watchAllFields.prcAncillary?.dealer as SelectElementModelFE).code as string,
            legalEntity: (watchAllFields.prcAncillary?.legalEntity as SelectElementModelFE).code as string,
        },
        commercialSegment: fromSingleGenericFeToSingleGenericBe(watchAllFields?.commercialSegment as CommercialSegmentSelectFE),
        relatedDiscountIncreasePercentage: watchAllFields.discountIncreasePercentage || "",
        relatedDiscountIncreaseFixed: Number(watchAllFields.discountIncreaseFixed?.toString().replace(",", ".")) * 100 || "",
        sellDesk: watchAllFields?.prcAncillary?.sellDesk as boolean,
        passageToCounterNeeded: watchAllFields?.prcAncillary?.passageToCounterNeeded as boolean,
        applicationTiming: watchAllFields?.applicationTiming === null ? null : watchAllFields?.applicationTiming === "PICK UP" ? "PICK_UP" : "DROP_OFF",
        toleranceTime: watchAllFields?.prcAncillary?.toleranceTime as number,
        flagResellableAtDeskPostRa: watchAllFields.flagResellableAtDeskPostRa ?? false,
        flagResellableInSubstitution: watchAllFields.flagResellableInSubstitution ?? false,
        flagDiscountApplicable: watchAllFields.flagDiscountApplicable ?? false,
        timeToChange: watchAllFields.timeToChange ?? null,
        timeToRemove: watchAllFields.timeToRemove ?? null,
        additionalField: watchAllFields.prcAncillary?.additionalField || null,
        tax: watchAllFields.prcAncillary?.tax as boolean,
        taxCode: watchAllFields.prcAncillary?.taxCode as string,
    };
};

export const buildAncillaryExceptionBaseDefinitionValues = (
    ancillaryException: AncillaryExceptionEntityResponse,
    commercialCategory: CommercialCategorySelectFE,
    commercialSegment: SelectElementModelFE,
    services: any,
    t?: any
): AncillaryExceptionBaseDefinitionFormValues => {
    return {
        id: ancillaryException.id,
        prcAncillary: {
            ...ancillaryException.prcAncillary,
            code: String(ancillaryException.prcAncillary?.id),
            ancillaryCode: String(ancillaryException.prcAncillary?.code),
            label: ancillaryException.prcAncillary?.nameAncillary as string,
            reservationFrom: ancillaryException.prcAncillary?.reservationFrom as string,
            reservationTo: ancillaryException.prcAncillary?.reservationTo as string,
            pickupFrom: ancillaryException.prcAncillary?.pickupFrom as string,
            pickupTo: ancillaryException.prcAncillary?.pickupTo as string,
            tpUnit: ancillaryException.prcAncillary?.tpUnit as string,
            currency: ancillaryException.prcAncillary?.currency as string,
            amountMin: ancillaryException.prcAncillary?.amountMin ? Number(i18nConvertNumberWithDecimals(Number(ancillaryException.prcAncillary.amountMin) / 100)) : null,
            amountMax: ancillaryException.prcAncillary?.amountMax ? Number(i18nConvertNumberWithDecimals(Number(ancillaryException.prcAncillary.amountMax) / 100)) : null,
            dealerId: ancillaryException.prcAncillary?.dealerId as number,
            chargingWay: ancillaryException.prcAncillary?.chargingWay as string,
            maxUnits: ancillaryException.prcAncillary?.maxUnits as number,
            chargeType: ancillaryException.prcAncillary?.chargeType as string,
            nameAncillary: ancillaryException.prcAncillary?.nameAncillary as string,
            description: ancillaryException.prcAncillary?.description as string,
            type: ancillaryException.prcAncillary?.type as string,
            //exceptionListPack: ancillaryException.prcAncillary?.exceptionListPack,
            subtype: ancillaryException.prcAncillary?.subtype as string,
            segments: fromGenericBeToGenericFe(ancillaryException.prcAncillary?.segments as SelectElementModelBE[]),
            cardinality: ancillaryException.prcAncillary?.cardinality as number,
            sellDesk: ancillaryException.prcAncillary?.sellDesk as boolean,
            passageToCounterNeeded: ancillaryException.prcAncillary?.passageToCounterNeeded as boolean,
            applicationTiming: ancillaryException.prcAncillary?.applicationTiming === null ? null : ancillaryException.prcAncillary?.applicationTiming === "PICK_UP" ? "PICK UP" : "DROP OFF",
            toleranceTime: ancillaryException.prcAncillary?.toleranceTime as number,
            flagResellableAtDeskPostRa: ancillaryException.prcAncillary?.flagResellableAtDeskPostRa ?? false,
            flagResellableInSubstitution: ancillaryException.prcAncillary?.flagResellableInSubstitution ?? false,
            flagDiscountApplicable: ancillaryException.prcAncillary?.flagDiscountApplicable ?? false,
            timeToChange: ancillaryException.prcAncillary?.timeToChange ?? null,
            timeToRemove: ancillaryException.prcAncillary?.timeToRemove ?? null,
            additionalField: ancillaryException.prcAncillary?.additionalField as string,
            tax: ancillaryException.prcAncillary?.tax as boolean,
            taxCode:  services.find((s: any) => s.code === ancillaryException.prcAncillary?.taxCode)?.name || "",
            isOneWay: ancillaryException.prcAncillary?.isOneWay as boolean,
            dealer: {
                code: ancillaryException.prcAncillary?.dealer as string,
                label: t('translation:dealers.'.concat(ancillaryException.prcAncillary?.dealer as string))
            },
            legalEntity: {
                code: ancillaryException.prcAncillary?.legalEntity as string,
                label: t('translation:legalEntities.'.concat(ancillaryException.prcAncillary?.legalEntity as string))
            },
        },
        dealer: {
            code: ancillaryException.dealer as string,
            label: t('translation:dealers.'.concat(ancillaryException.dealer as string))
        },
        legalEntity: {
            code: ancillaryException.legalEntity as string,
            label: t('translation:legalEntities.'.concat(ancillaryException.legalEntity as string))
        },

        discountIncreaseSelected: "discountIncreasePercentage",
        discountIncreasePercentage: ancillaryException.relatedDiscountIncreasePercentage,
        discountIncreaseFixed: i18nConvertNumberWithDecimals(Number(ancillaryException.relatedDiscountIncreaseFixed) / 100),
        name: ancillaryException.name,

// FIELDS FROM ANCILLARY
        commercialCategory: commercialCategory || null,
        note: ancillaryException.note || null,
        reservationFrom: ancillaryException.reservationFrom,
        reservationTo: ancillaryException.reservationTo,
        pickupFrom: ancillaryException.pickupFrom,
        pickupTo: ancillaryException.pickupTo,
        tpUnit: ancillaryException.tpUnit as string,
        currency: ancillaryException.currency,
        amountMin: ancillaryException.amountMin ? i18nConvertNumberWithDecimals(ancillaryException.amountMin / 100) : null,
        amountMax: ancillaryException.amountMax ? i18nConvertNumberWithDecimals(ancillaryException.amountMax / 100) : null,
        chargingWay: ancillaryException.chargingWay,
        chargeType: ancillaryException.chargeType,
        maxUnits: ancillaryException.timeUnitsMax,
        commercialSegment: commercialSegment || null,
        code: ancillaryException.code,
        nameAncillary: ancillaryException.prcAncillary?.nameAncillary as string, // TODO: implementazione multilanguage?
        description: ancillaryException.description,
        type: ancillaryException.type,
        exceptionListPack: {
            code: ancillaryException.exceptionListPack?.id as number,
            label: ancillaryException.exceptionListPack?.name as string
        },
        subtype: ancillaryException.subtype,
        cardinality: ancillaryException.cardinality,
        sellDesk: ancillaryException.sellDesk,
        passageToCounterNeeded: ancillaryException.passageToCounterNeeded,
        applicationTiming: ancillaryException.prcAncillary?.applicationTiming === null ? null : ancillaryException.prcAncillary?.applicationTiming === "PICK_UP" ? "PICK UP" : "DROP OFF",
        toleranceTime: ancillaryException.toleranceTime,
        flagResellableAtDeskPostRa: ancillaryException.flagResellableAtDeskPostRa ?? false,
        flagResellableInSubstitution: ancillaryException.flagResellableInSubstitution ?? false,
        flagDiscountApplicable: ancillaryException.flagDiscountApplicable ?? false,
        timeToChange: ancillaryException.timeToChange ?? null,
        timeToRemove: ancillaryException.timeToRemove ?? null,
        additionalField: ancillaryException.additionalField,
        tax: ancillaryException.tax,
        taxCode: services.find((s: any) => s.code === ancillaryException.taxCode)?.name || null,

    };
};